import { translateBlockValue } from 'i18n';
import { BudgetExpenseOrIncome, BudgetSections } from 'business/constants';
import {
    Colors,
    SPLIT_BY,
    JOIN_BY,
    CADRAGE,
} from 'routes/BudgetModule/config/constants';
import { currencyFormatter } from 'helpers/formatHelper';
import { CellRendererParams } from '../config/types';

const euroFormatter = new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
});

const percentFormatter = (value: number): string =>
    `${(value * 100).toFixed(0)}%`;

const euroFormat = (value: number): string =>
    euroFormatter.format(Number(value));

const getNumericFormat = (value: number, isPercentColum: boolean): string => {
    if (Number.isNaN(value)) return '';
    if (value !== undefined && !Number.isNaN(value)) {
        if (isPercentColum) return percentFormatter(value);
        return euroFormat(value);
    }
    return translateBlockValue(value.toString(), true);
};

export const getFormat = (
    value: ResponseDataValueT,
    isPercentColum: boolean = false,
): string => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'number')
        return getNumericFormat(value, isPercentColum);
    if (typeof value === 'string') return translateBlockValue(value, true);
    return translateBlockValue(value.toString(), true);
};

export const translateMultipleName = (cell: string): string =>
    cell
        .split(SPLIT_BY)
        .map((c) => translateBlockValue(c, true))
        .join(JOIN_BY);

/**
 * Check if a given string has the shape of a metric by year
 * e.g. "budgetLine.software_id..2023"
 */
export const checkIsMetricByYear = (field: string) => {
    const fieldParts = field.split(SPLIT_BY);
    if (fieldParts.length !== 2) return false;
    const isYear = Boolean(fieldParts[fieldParts.length - 1].match(/^\d{4}$/));
    return isYear;
};

export const getColnameValueByYear = (colname: string, year: number): string =>
    [colname, year].join(SPLIT_BY);

export const getDepassementColor = (cellValue: number): Colors => {
    if (cellValue >= 0) return Colors.RED;
    if (Number.isNaN(cellValue)) return Colors.BLACK;
    return Colors.GREEN;
};

export const getDepassementTooltip = (
    params: CellRendererParams<ResponseDataT, unknown>,
): string => {
    const cadrageValue = Number(params?.data?.[CADRAGE]) || 0;
    const expenseValue = Number(params?.data?.expense) || 0;
    return currencyFormatter(expenseValue - cadrageValue);
};

export const getRealizationRateColor = (
    cellValue: number,

    isExpense: boolean,
): Colors => {
    if (cellValue >= 0.9 && cellValue <= 1)
        return isExpense ? Colors.GREEN : Colors.RED;
    if (cellValue >= 0.7 && cellValue < 0.9) return Colors.ORANGE;
    return isExpense ? Colors.RED : Colors.GREEN;
};

/**
Translate the response of a request based on his type (number, object, float etc)
Decimal of float are taken away
String are translated
For object having the format {value, label}, only the label is translated
 */
const translationDataValue = (
    value: ResponseDataValueT,
    translate: (str: string) => string,
): ResponseDataValueT => {
    // TODO [typing getData hooks] try to remove this non-null assertion
    if (typeof value === 'object' && 'label' in value!)
        return translate(value.label);
    if (typeof value === 'string') return translate(value);
    return value;
};
/**
Those are special requirements for importing the CSV into JVS software
for a given string
*/
const jvsTranslationString = (value: string): string => {
    if (value === 'unknown') return '';
    if (value === BudgetSections.INVESTMENT) return 'I';
    if (value === BudgetSections.OPERATING) return 'F';
    if (value === BudgetExpenseOrIncome.EXPENSE) return 'D';
    if (value === BudgetExpenseOrIncome.INCOME) return 'R';
    return translateBlockValue(value);
};

/**
Those are special requirements for importing the CSV into CIRIL software
for a given string
*/
const cirilTranslationString = (value: string): string => {
    if (value === 'unknown') return '';
    if (value === BudgetExpenseOrIncome.EXPENSE) return 'D';
    if (value === BudgetExpenseOrIncome.INCOME) return 'R';
    return translateBlockValue(value);
};

/**
Those are special requirements for importing the CSV into SEDIT software
for a given string
*/
const seditTranslationString = (value: string): string => {
    if (value === 'unknown') return '';
    if (value === BudgetExpenseOrIncome.EXPENSE) return 'D';
    if (value === BudgetExpenseOrIncome.INCOME) return 'R';
    if (value === BudgetSections.INVESTMENT) return 'I';
    if (value === BudgetSections.OPERATING) return 'F';
    return translateBlockValue(value);
};

/**
 * Apply JVS requirements to the entire response data array
 */
export const jvsTranslationDataValue = (
    value: ResponseDataValueT,
): ResponseDataValueT => translationDataValue(value, jvsTranslationString);

/**
Apply CIRIL requirements to the entire response data array
*/
export const cirilTranslationDataValue = (
    value: ResponseDataValueT,
): ResponseDataValueT => translationDataValue(value, cirilTranslationString);

/**
Apply SEDIT requirements to the entire response data array
*/
export const seditTranslationDataValue = (
    value: ResponseDataValueT,
): ResponseDataValueT => translationDataValue(value, seditTranslationString);

export const getFieldByExercice = (field: string, exercice: number): string =>
    `${field}${SPLIT_BY}${exercice}`;
